import * as Sentry from '@sentry/react'
import { Suspense } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { SWRConfig } from 'swr'
import { CommonLayout } from './components/CommonLayout'
import { ErrorFallback } from './components/ErrorFallback'
import { ScrollToTop } from './components/ScrollToTop'
import { ToastHost } from './components/ToastHost'
import { TopProgressBar } from './components/TopProgressBar'
import { AppConfigProvider } from './contexts/AppConfigContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { buildLoginRedirectPath } from './hooks/usePostAuthNavigation/utils'
import { lazyWithRetry } from './lib/lazyWithRetry'
import { InstanceErrorFallback } from './screens/InstanceScreen/components/InstanceErrorFallback'
import { AuthError } from './services/auth/AuthService'

// 動的インポート（Viteが自動的にチャンク分割）
const AvatarDetailScreen = lazyWithRetry(() =>
  import('./screens/AvatarDetailScreen').then((m) => ({ default: m.AvatarDetailScreen })),
)
const AvatarSettingsScreen = lazyWithRetry(() =>
  import('./screens/AvatarSettingsScreen').then((m) => ({ default: m.AvatarSettingsScreen })),
)
const AvatarUploadScreen = lazyWithRetry(() =>
  import('./screens/AvatarUploadScreen').then((m) => ({ default: m.AvatarUploadScreen })),
)
const CLILoginScreen = lazyWithRetry(() =>
  import('./screens/CLILoginScreen').then((m) => ({ default: m.CLILoginScreen })),
)
const EventsScreen = lazyWithRetry(() =>
  import('./screens/EventsScreen').then((m) => ({ default: m.EventsScreen })),
)
const EventCreateScreen = lazyWithRetry(() =>
  import('./screens/EventCreateScreen').then((m) => ({ default: m.EventCreateScreen })),
)
const EventDetailScreen = lazyWithRetry(() =>
  import('./screens/EventDetailScreen').then((m) => ({ default: m.EventDetailScreen })),
)
const EventEditScreen = lazyWithRetry(() =>
  import('./screens/EventEditScreen').then((m) => ({ default: m.EventEditScreen })),
)
const ForgotPasswordScreen = lazyWithRetry(() =>
  import('./screens/ForgotPasswordScreen').then((m) => ({ default: m.ForgotPasswordScreen })),
)
const FriendsScreen = lazyWithRetry(() =>
  import('./screens/FriendsScreen').then((m) => ({ default: m.FriendsScreen })),
)
const HomeScreen = lazyWithRetry(() =>
  import('./screens/HomeScreen').then((m) => ({ default: m.HomeScreen })),
)
const InstanceScreen = lazyWithRetry(() =>
  import('./screens/InstanceScreen').then((m) => ({ default: m.InstanceScreen })),
)
const LoginScreen = lazyWithRetry(() =>
  import('./screens/LoginScreen').then((m) => ({ default: m.LoginScreen })),
)
const ItemDetailScreen = lazyWithRetry(() =>
  import('./screens/ItemDetailScreen').then((m) => ({ default: m.ItemDetailScreen })),
)
const MyItemsScreen = lazyWithRetry(() =>
  import('./screens/MyItemsScreen').then((m) => ({ default: m.MyItemsScreen })),
)
const ProfileScreen = lazyWithRetry(() =>
  import('./screens/ProfileScreen').then((m) => ({ default: m.ProfileScreen })),
)
const ResendVerificationScreen = lazyWithRetry(() =>
  import('./screens/ResendVerificationScreen').then((m) => ({
    default: m.ResendVerificationScreen,
  })),
)
const ResetPasswordScreen = lazyWithRetry(() =>
  import('./screens/ResetPasswordScreen').then((m) => ({ default: m.ResetPasswordScreen })),
)
const SettingsScreen = lazyWithRetry(() =>
  import('./screens/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
)
const SharedFilesScreen = lazyWithRetry(() =>
  import('./screens/SharedFilesScreen').then((m) => ({ default: m.SharedFilesScreen })),
)
const VerifyEmailScreen = lazyWithRetry(() =>
  import('./screens/VerifyEmailScreen').then((m) => ({ default: m.VerifyEmailScreen })),
)
const VerifyNotificationEmailScreen = lazyWithRetry(() =>
  import('./screens/VerifyNotificationEmailScreen').then((m) => ({
    default: m.VerifyNotificationEmailScreen,
  })),
)
const VerifyPendingScreen = lazyWithRetry(() =>
  import('./screens/VerifyPendingScreen').then((m) => ({ default: m.VerifyPendingScreen })),
)
const VRoidCallbackErrorScreen = lazyWithRetry(() =>
  import('./screens/VRoidCallbackErrorScreen').then((m) => ({
    default: m.VRoidCallbackErrorScreen,
  })),
)
const VRoidCallbackSuccessScreen = lazyWithRetry(() =>
  import('./screens/VRoidCallbackSuccessScreen').then((m) => ({
    default: m.VRoidCallbackSuccessScreen,
  })),
)
const WorldDetailScreen = lazyWithRetry(() =>
  import('./screens/WorldDetailScreen').then((m) => ({ default: m.WorldDetailScreen })),
)
const UserProfileScreen = lazyWithRetry(() =>
  import('./screens/UserProfileScreen').then((m) => ({ default: m.UserProfileScreen })),
)
const WorldsScreen = lazyWithRetry(() =>
  import('./screens/WorldsScreen').then((m) => ({ default: m.WorldsScreen })),
)
const MarketplaceScreen = lazyWithRetry(() =>
  import('./screens/MarketplaceScreen').then((m) => ({ default: m.MarketplaceScreen })),
)
const MarketplaceItemDetailScreen = lazyWithRetry(() =>
  import('./screens/MarketplaceItemDetailScreen').then((m) => ({
    default: m.MarketplaceItemDetailScreen,
  })),
)
const DevScrambleScreen = lazyWithRetry(() =>
  import('./screens/DevScrambleScreen').then((m) => ({ default: m.DevScrambleScreen })),
)
const DevVRDashboardScreen = lazyWithRetry(() =>
  import('./screens/DevVRDashboardScreen').then((m) => ({ default: m.DevVRDashboardScreen })),
)

/**
 * 公開ルート用レイアウト（サイドバーなし）
 */
function PublicLayout() {
  const location = useLocation()
  return (
    <Suspense key={location.pathname} fallback={<TopProgressBar />}>
      <Outlet />
    </Suspense>
  )
}

/**
 * サイドバー付きレイアウト（CommonLayout を1つだけマウント）
 * 閲覧ルートと保護ルートの両方で共有し、遷移時の再マウントを防止
 */
function SidebarLayout() {
  const location = useLocation()
  return (
    <CommonLayout>
      <Suspense key={location.pathname} fallback={<TopProgressBar />}>
        <Outlet />
      </Suspense>
    </CommonLayout>
  )
}

/**
 * 認証チェック用アウトレット
 * isLoading 中はプログレスバー、未認証時は /login へリダイレクト
 * （ログイン後に元のページへ戻れるよう redirect クエリを付与する）
 */
function ProtectedOutlet() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return <TopProgressBar />
  if (!isAuthenticated) {
    return <Navigate to={buildLoginRedirectPath(location.pathname + location.search)} replace />
  }
  return <Outlet />
}

/**
 * インスタンス画面用レイアウト
 * instanceId変更時にErrorBoundary以下を完全に再マウントし、WebSocket・LiveKit等のクリーンアップを確実に実行する
 */
function InstanceScreenLayout() {
  const { instanceId } = useParams<{ instanceId: string }>()
  return (
    // インスタンス内の例外をここで封じ込め、最上位 ErrorBoundary による全画面クラッシュ
    // （＝インスタンスから追い出される）を防ぐ。instanceId 変更時にboundaryごと再マウントしてエラー状態をリセットする。
    <Sentry.ErrorBoundary key={instanceId} fallback={InstanceErrorFallback}>
      <Suspense fallback={<TopProgressBar />}>
        <InstanceScreen />
      </Suspense>
    </Sentry.ErrorBoundary>
  )
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* 公開ルート（サイドバーなし） */}
        <Route element={<PublicLayout />}>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/cli-login" element={<CLILoginScreen />} />
          <Route path="/auth/verify-email" element={<VerifyEmailScreen />} />
          <Route
            path="/auth/verify-notification-email"
            element={<VerifyNotificationEmailScreen />}
          />
          <Route path="/auth/verify-pending" element={<VerifyPendingScreen />} />
          <Route path="/auth/resend-verification" element={<ResendVerificationScreen />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/auth/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/auth/vroid/success" element={<VRoidCallbackSuccessScreen />} />
          <Route path="/auth/vroid/error" element={<VRoidCallbackErrorScreen />} />
        </Route>

        {/* 開発ツール（DEVのみ） */}
        {import.meta.env.DEV && (
          <Route element={<PublicLayout />}>
            <Route path="/dev/scramble-avatar" element={<DevScrambleScreen />} />
          </Route>
        )}
        {/* VRダッシュボードの見た目確認（DEVのみ・全画面） */}
        {import.meta.env.DEV && (
          <Route path="/dev/vr-dashboard/:instanceId?" element={<DevVRDashboardScreen />} />
        )}

        {/* インスタンス画面（サイドバーなし、フルスクリーン） */}
        {/* URLを直接知っているユーザーだけが到達できる導線として、ゲスト入室を許可する唯一のルート */}
        <Route path="/instance/:instanceId" element={<InstanceScreenLayout />} />

        {/* サイドバー付きルート（CommonLayout を1つだけマウント） */}
        <Route element={<SidebarLayout />}>
          {/* 閲覧ルート（認証不要） */}
          {/* ワールド・イベント・マーケットプレイスはゲストにも見せる。ただし各画面で
              インスタンスへの導線はログイン必須にしてあり、ゲストがインスタンスを
              発見・入室できないようにしている */}
          <Route path="/" element={<HomeScreen />} />
          <Route path="/worlds" element={<WorldsScreen />} />
          <Route path="/world/:worldId" element={<WorldDetailScreen />} />
          <Route path="/events" element={<EventsScreen />} />
          <Route path="/events/:eventId" element={<EventDetailScreen />} />
          <Route path="/marketplace" element={<MarketplaceScreen />} />
          <Route path="/marketplace/:itemId" element={<MarketplaceItemDetailScreen />} />
          {/* 保護ルート（認証必須） */}
          <Route element={<ProtectedOutlet />}>
            <Route path="/user/:userId" element={<UserProfileScreen />} />
            <Route path="/my-worlds" element={<Navigate to="/worlds?tab=my-worlds" replace />} />
            <Route path="/events/new" element={<EventCreateScreen />} />
            <Route path="/events/:eventId/edit" element={<EventEditScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/settings/avatar" element={<AvatarSettingsScreen />} />
            <Route path="/settings/avatar/upload" element={<AvatarUploadScreen />} />
            <Route path="/settings/avatar/:avatarId" element={<AvatarDetailScreen />} />
            <Route path="/myitems" element={<MyItemsScreen />} />
            <Route path="/my-items/:itemId" element={<ItemDetailScreen />} />
            <Route path="/friends" element={<FriendsScreen />} />
            <Route path="/shared-files" element={<SharedFilesScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

// 認証エラー（リフレッシュ失敗後の401等）はSWRリトライしない
const shouldRetryOnError = (err: Error): boolean => !(err instanceof AuthError)

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <SWRConfig value={{ shouldRetryOnError }}>
        <AppConfigProvider>
          <AuthProvider>
            <AppRoutes />
            <ToastHost />
          </AuthProvider>
        </AppConfigProvider>
      </SWRConfig>
    </Sentry.ErrorBoundary>
  )
}

export default App
